import { zodResolver } from "@hookform/resolvers/zod";
import cloneDeep from "lodash.clonedeep";
import merge from "lodash.merge";
import { useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { LoanDetailsController } from "services/loanDetails/controller";
import { FormRenderer, useAccordionWizard } from "stories";
import { AccordionForm } from "stories/components/accordionForm";
import { useFileUpload } from "utils/customHooks/useFileUpload";
import { useToast } from "utils/customHooks/useToast";
import { z } from "zod";
import { borrowerFormFields, getFormDefaultValue } from "./formStructure";

const loanDetailsController = LoanDetailsController();

export const removeEmptyValues = (body) => {
  if (typeof body === "object") {
    for (let key in body) {
      if (
        body[key] === null ||
        (typeof body[key] === "number" && isNaN(body[key])) ||
        body[key] === ""
      ) {
        delete body[key];
      }
      if (body[key] instanceof FileList || body[key] instanceof File) continue;
      if (typeof body[key] === "object") {
        body[key] = removeEmptyValues(body[key]);
        if (Object.keys(body[key]).length === 0) delete body[key];
      }
      if (typeof body[key] === "object" && Object.keys(body[key]).length === 0)
        delete body[key];
    }
  }
  return body;
};

const schema = z
  .object({
    name: z.string().min(1, "This field is required"),
    corporate_structure: z.string().min(1, "This field is required."),
  })
  .passthrough();
export const LoanDetailsBorrowerQueryKey = "loan-details-borrower";
const LoanDetailsBorrower = () => {
  const { next } = useAccordionWizard();
  const { showToast } = useToast();
  const params = useParams();
  const queryClient = useQueryClient();
  const queryKey = [LoanDetailsBorrowerQueryKey, { id: params.id }];
  const { handleFileFields } = useFileUpload();
  const form = useForm({
    defaultValues: merge(cloneDeep(getFormDefaultValue()), {
      partners: [{ opportunity_id: parseInt(params.id) }],
    }),
    resolver: zodResolver(schema),
  });

  const { isLoading: fetchLoading } = useQuery({
    queryKey,
    queryFn: loanDetailsController.getBorrower.bind(null, params.id),
    onSuccess: (data) => {
      const fields = merge(getFormDefaultValue(), data);
      if (fields.corporate_structure === null) {
        fields.corporate_structure = "";
      }
      form.reset(fields, { keepDefaultValues: true });
    },
  });

  const handleBorrowerSubmit = async (values) => {
    return handleFileFields(values).then((values) => {
      values = removeEmptyValues(values);
      values.opportunityId = params.id;
      return loanDetailsController.updateBorrower(values);
    });
  };

  const { mutate, isLoading: postLoading } = useMutation(handleBorrowerSubmit, {
    onSuccess: () => {
      showToast("Borrower Updated Successfully!", "success");
      next();
      queryClient.invalidateQueries(queryKey);
    },
  });

  return (
    <AccordionForm
      accordionKey="borrower"
      accordionTitle="Borrower"
      form={form}
      isPostLoading={postLoading}
      isPreLoading={fetchLoading}
      onSubmit={mutate}
    >
      <FormRenderer fields={borrowerFormFields} />
    </AccordionForm>
  );
};

export default LoanDetailsBorrower;
